// Percorso file: src/components/games/TabellaFinale.jsx
//
// LA CHIUSURA — cantiere GAMES, fetta G2, PIANO §2 fase 6, §3 «Punteggio»
// e «Correzione a fine partita» (Strada A): il risultato scritto A MANO
// (us–them, interi ≥ 0) e la TABELLA DEI TOTALI per convocato — minuti,
// gol, gialli, rosso — tutti EDITABILI. Si correggono i totali, non la
// cronologia. Componente di PRESENTAZIONE: `score` e `righe` vivono nel
// padre, che riceve ogni modifica per callback e valida al salvataggio con
// `corpoSalvataggio` (regolePartita.js).
//
// LA RESA (inventata, dichiarata nel referto): sul telefono una tabella a
// cinque colonne con comandi dentro non regge, quindi UNA CARD PER
// CONVOCATO con quattro campi; gol e gialli con lo STEPPER [−] n [+] (sono
// numeri piccoli, si toccano meglio di una tastiera), minuti e risultato con
// lo stepper INTORNO A UN CAMPO DIGITABILE (possono essere grandi). Il rosso
// è la spunta di casa (`qpsc-check`).
//
// ⚠️ Gli input numerici sono `type="text"` + `inputMode="numeric"`, NON
// `type="number"`: il selettore (0,4,1) di EvaluationSheetModal.css veste
// ogni input che non sia `type="text"` con fondo e bordo suoi, fuori
// palette; il tipo testo lo esclude per costruzione e la tastiera numerica
// arriva lo stesso da `inputMode`. Il valore resta una STRINGA nello stato
// (ciò che il coach digita) finché non si salva.
//
// La divergenza fra risultato scritto e gol assegnati si MOSTRA senza
// allarme e non si forza (PIANO §3, §7).

import React from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import { golAssegnati, interoNonNegativo } from "./regolePartita";
import "../AdminTools.css";
import "./GamesMatch.css";

/** Il passo dello stepper su un valore che può essere testo digitato:
 *  un valore illeggibile riparte da 0. */
function passo(valore, delta) {
  const n = interoNonNegativo(valore);
  const base = n === null ? 0 : n;
  return String(Math.max(0, base + delta));
}

/** [−] n [+] con, in mezzo, il numero (sola lettura) o un campo digitabile. */
function Stepper({ valore, onValore, digitabile, etichetta }) {
  const n = interoNonNegativo(valore);
  return (
    <span className="qpsc-games-stepper">
      <button
        type="button"
        className="qpsc-games-step"
        onClick={() => onValore(passo(valore, -1))}
        disabled={n !== null && n <= 0}
        aria-label={`${etichetta}: one less`}
      >
        <FaMinus />
      </button>
      {digitabile ? (
        <input
          className="qpsc-input qpsc-games-num"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={valore}
          onChange={(e) => onValore(e.target.value)}
          aria-label={etichetta}
        />
      ) : (
        <span className="qpsc-games-stepper__value" aria-label={etichetta}>
          {n === null ? "?" : n}
        </span>
      )}
      <button
        type="button"
        className="qpsc-games-step"
        onClick={() => onValore(passo(valore, 1))}
        aria-label={`${etichetta}: one more`}
      >
        <FaPlus />
      </button>
    </span>
  );
}

export default function TabellaFinale({ squadra, avversario, score, righe, onScore, onRiga }) {
  const assegnati = golAssegnati(righe);
  // Per la sola frase «differs from the score»: un risultato non ancora
  // scritto non è uno 0 (stesso criterio di `vuoto` in corpoSalvataggio).
  const us = String(score.us ?? "").trim() === "" ? null : interoNonNegativo(score.us);

  return (
    <>
      <section className="qpsc-games-score" aria-label="Final score">
        <h2 className="qpsc-games-score__title">Final score</h2>
        <div className="qpsc-games-score__row">
          <div className="qpsc-games-score__side">
            <span className="qpsc-games-score__label" title={squadra}>
              {squadra}
            </span>
            <Stepper
              valore={score.us}
              onValore={(v) => onScore("us", v)}
              digitabile
              etichetta="Our goals"
            />
          </div>
          <span className="qpsc-games-score__dash" aria-hidden="true">
            &ndash;
          </span>
          <div className="qpsc-games-score__side">
            <span className="qpsc-games-score__label" title={avversario || "Opponent"}>
              {avversario || "Opponent"}
            </span>
            <Stepper
              valore={score.them}
              onValore={(v) => onScore("them", v)}
              digitabile
              etichetta="Their goals"
            />
          </div>
        </div>
        <p className="qpsc-games-score__note">
          Goals assigned to players: {assegnati}
          {us !== null && us !== assegnati ? " — differs from the score, and that's allowed." : ""}
        </p>
      </section>

      {righe.length === 0 ? (
        <p className="qpsc-games-rows__empty">No players in this match.</p>
      ) : (
        <div className="qpsc-games-rows">
          {righe.map((r) => (
            <article key={r.playerId} className="qpsc-games-row">
              <span className="qpsc-games-row__name">{r.nome}</span>
              <div className="qpsc-games-row__fields">
                <div className="qpsc-games-row__field">
                  <span className="qpsc-games-row__label">Minutes</span>
                  <Stepper
                    valore={r.minutes}
                    onValore={(v) => onRiga(r.playerId, "minutes", v)}
                    digitabile
                    etichetta={`${r.nome}: minutes`}
                  />
                </div>
                <div className="qpsc-games-row__field">
                  <span className="qpsc-games-row__label">Goals</span>
                  <Stepper
                    valore={r.goals}
                    onValore={(v) => onRiga(r.playerId, "goals", v)}
                    etichetta={`${r.nome}: goals`}
                  />
                </div>
                <div className="qpsc-games-row__field">
                  <span className="qpsc-games-row__label">Yellow</span>
                  <Stepper
                    valore={r.yellowCards}
                    onValore={(v) => onRiga(r.playerId, "yellowCards", v)}
                    etichetta={`${r.nome}: yellow cards`}
                  />
                </div>
                <div className="qpsc-games-row__field">
                  <span className="qpsc-games-row__label">Red</span>
                  <label className="qpsc-check">
                    <input
                      className="qpsc-check__input"
                      type="checkbox"
                      checked={Boolean(r.redCard)}
                      onChange={(e) => onRiga(r.playerId, "redCard", e.target.checked)}
                    />
                    <span className="qpsc-check__box" aria-hidden="true" />
                    {r.redCard ? "Sent off" : "No"}
                  </label>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
